import React from "react";
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Button,
    Spinner,
    FormGroup,
    Label,
    Input
} from "reactstrap";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

// Long timeout for large spreadsheet imports (5 minutes)
const IMPORT_TIMEOUT = 300000;

class AdminImport extends React.Component {
    state = {
        // Song data — xlsx
        songXlsxFiles: null,
        songXlsxLoading: false,
        songXlsxResult: null,

        // Song data — zip
        songZipFile: null,
        songZipLoading: false,
        songZipResult: null,

        // Metadata — xlsx
        metaXlsxFiles: null,
        metaXlsxLoading: false,
        metaXlsxResult: null,

        // Metadata — zip
        metaZipFile: null,
        metaZipLoading: false,
        metaZipResult: null,

        unauthorized: false,
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (!parsed?.user?.isAdmin) {
                this.setState({ unauthorized: true });
            }
        } catch (e) {
            this.setState({ unauthorized: true });
        }
    }

    // ── Song data xlsx ────────────────────────────────────────────────────────

    handleSongXlsxSubmit = (e) => {
        e.preventDefault();
        const { songXlsxFiles } = this.state;
        if (!songXlsxFiles || songXlsxFiles.length === 0) return;

        const formData = new FormData();
        for (const file of songXlsxFiles) {
            formData.append("files", file);
        }

        this.setState({ songXlsxLoading: true, songXlsxResult: null });
        Api.post("/api/import/spreadsheets", formData, { timeout: IMPORT_TIMEOUT })
            .then(res => {
                this.setState({
                    songXlsxResult: { ok: true, message: res.data },
                    songXlsxLoading: false,
                    songXlsxFiles: null
                });
                document.getElementById("songXlsxInput").value = "";
            })
            .catch(err => {
                const msg = err.response?.data || err.message || "Upload failed";
                this.setState({
                    songXlsxResult: { ok: err.response?.status === 207, message: msg },
                    songXlsxLoading: false
                });
            });
    };

    // ── Song data zip ─────────────────────────────────────────────────────────

    handleSongZipSubmit = (e) => {
        e.preventDefault();
        const { songZipFile } = this.state;
        if (!songZipFile) return;

        const formData = new FormData();
        formData.append("file", songZipFile);

        this.setState({ songZipLoading: true, songZipResult: null });
        Api.post("/api/import/spreadsheets/zip", formData, { timeout: IMPORT_TIMEOUT })
            .then(res => {
                this.setState({
                    songZipResult: { ok: true, message: res.data },
                    songZipLoading: false,
                    songZipFile: null
                });
                document.getElementById("songZipInput").value = "";
            })
            .catch(err => {
                const msg = err.response?.data || err.message || "Upload failed";
                this.setState({
                    songZipResult: { ok: err.response?.status === 207, message: msg },
                    songZipLoading: false
                });
            });
    };

    // ── Metadata xlsx ─────────────────────────────────────────────────────────

    handleMetaXlsxSubmit = (e) => {
        e.preventDefault();
        const { metaXlsxFiles } = this.state;
        if (!metaXlsxFiles || metaXlsxFiles.length === 0) return;

        const formData = new FormData();
        for (const file of metaXlsxFiles) {
            formData.append("files", file);
        }

        this.setState({ metaXlsxLoading: true, metaXlsxResult: null });
        Api.post("/api/import/metadata/spreadsheets", formData, { timeout: IMPORT_TIMEOUT })
            .then(res => {
                this.setState({
                    metaXlsxResult: { ok: true, message: res.data },
                    metaXlsxLoading: false,
                    metaXlsxFiles: null
                });
                document.getElementById("metaXlsxInput").value = "";
            })
            .catch(err => {
                const msg = err.response?.data || err.message || "Upload failed";
                this.setState({
                    metaXlsxResult: { ok: err.response?.status === 207, message: msg },
                    metaXlsxLoading: false
                });
            });
    };

    // ── Metadata zip ──────────────────────────────────────────────────────────

    handleMetaZipSubmit = (e) => {
        e.preventDefault();
        const { metaZipFile } = this.state;
        if (!metaZipFile) return;

        const formData = new FormData();
        formData.append("file", metaZipFile);

        this.setState({ metaZipLoading: true, metaZipResult: null });
        Api.post("/api/import/metadata/spreadsheets/zip", formData, { timeout: IMPORT_TIMEOUT })
            .then(res => {
                this.setState({
                    metaZipResult: { ok: true, message: res.data },
                    metaZipLoading: false,
                    metaZipFile: null
                });
                document.getElementById("metaZipInput").value = "";
            })
            .catch(err => {
                const msg = err.response?.data || err.message || "Upload failed";
                this.setState({
                    metaZipResult: { ok: err.response?.status === 207, message: msg },
                    metaZipLoading: false
                });
            });
    };

    // ── Helpers ───────────────────────────────────────────────────────────────

    renderResult = (result) => {
        if (!result) return null;
        return (
            <div className={`alert ${result.ok ? "alert-success" : "alert-danger"} mt-3`} role="alert">
                <pre style={{ whiteSpace: "pre-wrap", marginBottom: 0, fontSize: "0.85rem" }}>
                    {result.message}
                </pre>
            </div>
        );
    };

    renderUploadPanel = ({ title, description, inputId, accept, multiple, files, loading, result, onFileChange, onSubmit }) => {
        const hasFiles = multiple ? (files && files.length > 0) : !!files;
        return (
            <div className="mb-4">
                <h6 className="text-muted mb-1">{title}</h6>
                <p className="small text-muted mb-2">{description}</p>
                <form onSubmit={onSubmit}>
                    <FormGroup>
                        <Input
                            id={inputId}
                            type="file"
                            accept={accept}
                            multiple={multiple}
                            onChange={onFileChange}
                            disabled={loading}
                        />
                        {multiple && files && files.length > 0 && (
                            <small className="text-muted">{files.length} file{files.length !== 1 ? "s" : ""} selected</small>
                        )}
                    </FormGroup>
                    <Button
                        color="primary"
                        size="sm"
                        type="submit"
                        disabled={loading || !hasFiles}
                    >
                        {loading ? (
                            <><Spinner size="sm" className="mr-2" />Importing…</>
                        ) : (
                            "Import"
                        )}
                    </Button>
                </form>
                {this.renderResult(result)}
            </div>
        );
    };

    render() {
        if (!sessionStorage.getItem("customer")) {
            return sessionEnded();
        }
        if (this.state.unauthorized) return <Navigate to="/" />;

        const {
            songXlsxFiles, songXlsxLoading, songXlsxResult,
            songZipFile,   songZipLoading,   songZipResult,
            metaXlsxFiles, metaXlsxLoading,  metaXlsxResult,
            metaZipFile,   metaZipLoading,   metaZipResult,
        } = this.state;

        return (
            <>
                <SeoNoIndex noindex={true} />

                <div className="position-relative">
                    <section className="section section-lg section-shaped pb-250">
                        <div className="shape shape-style-1 shape-default">
                            <span/><span/><span/><span/><span/><span/><span/><span/><span/>
                        </div>
                        <Container className="py-lg-md d-flex">
                            <div className="col px-0">
                                <Row>
                                    <Col lg="8">
                                        <h1 className="display-3 text-white">
                                            Admin <span>Spreadsheet Import</span>
                                        </h1>
                                    </Col>
                                </Row>
                            </div>
                        </Container>
                        <div className="separator separator-bottom separator-skew">
                            <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                                <polygon className="fill-white" points="2560 0 2560 100 0 100"/>
                            </svg>
                        </div>
                    </section>
                </div>

                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg="12">

                                <Card className="shadow border-0 mb-4">
                                    <CardBody className="py-3 px-4">
                                        <Button color="link" href="/admin" className="p-0">
                                            &larr; Back to Admin Menu
                                        </Button>
                                    </CardBody>
                                </Card>

                                <Row>
                                    {/* ── Song Data Import ─────────────────────────────── */}
                                    <Col lg="6" className="mb-4">
                                        <Card className="shadow border-0 h-100">
                                            <CardBody className="py-4">
                                                <h4 className="mb-1">Song Data Import</h4>
                                                <p className="text-muted small mb-4">
                                                    Import song, song-book association and scripture-reference data
                                                    from spreadsheets. Each <code>.xlsx</code> file must contain a
                                                    sheet named <strong>Songs</strong>.
                                                </p>

                                                <hr />

                                                {this.renderUploadPanel({
                                                    title: "Upload .xlsx files",
                                                    description: "Select one or more .xlsx spreadsheet files to import.",
                                                    inputId: "songXlsxInput",
                                                    accept: ".xlsx",
                                                    multiple: true,
                                                    files: songXlsxFiles,
                                                    loading: songXlsxLoading,
                                                    result: songXlsxResult,
                                                    onFileChange: e => this.setState({ songXlsxFiles: e.target.files, songXlsxResult: null }),
                                                    onSubmit: this.handleSongXlsxSubmit
                                                })}

                                                <hr />

                                                {this.renderUploadPanel({
                                                    title: "Upload zip of .xlsx files",
                                                    description: "Select a single .zip containing one or more .xlsx spreadsheet files.",
                                                    inputId: "songZipInput",
                                                    accept: ".zip",
                                                    multiple: false,
                                                    files: songZipFile,
                                                    loading: songZipLoading,
                                                    result: songZipResult,
                                                    onFileChange: e => this.setState({ songZipFile: e.target.files[0] || null, songZipResult: null }),
                                                    onSubmit: this.handleSongZipSubmit
                                                })}
                                            </CardBody>
                                        </Card>
                                    </Col>

                                    {/* ── Metadata Import ──────────────────────────────── */}
                                    <Col lg="6" className="mb-4">
                                        <Card className="shadow border-0 h-100">
                                            <CardBody className="py-4">
                                                <h4 className="mb-1">Song Metadata Import</h4>
                                                <p className="text-muted small mb-4">
                                                    Import musical metadata (key signature, time signature, tempo,
                                                    meter) for existing song-book entries. Each <code>.xlsx</code> file
                                                    must contain a sheet named <strong>Metadata</strong>. The filename
                                                    (minus <em>.xlsx</em> and any <em> Song Metadata</em> suffix)
                                                    is matched to the song book title.
                                                </p>

                                                <hr />

                                                {this.renderUploadPanel({
                                                    title: "Upload .xlsx files",
                                                    description: "Select one or more metadata .xlsx spreadsheet files to import.",
                                                    inputId: "metaXlsxInput",
                                                    accept: ".xlsx",
                                                    multiple: true,
                                                    files: metaXlsxFiles,
                                                    loading: metaXlsxLoading,
                                                    result: metaXlsxResult,
                                                    onFileChange: e => this.setState({ metaXlsxFiles: e.target.files, metaXlsxResult: null }),
                                                    onSubmit: this.handleMetaXlsxSubmit
                                                })}

                                                <hr />

                                                {this.renderUploadPanel({
                                                    title: "Upload zip of .xlsx files",
                                                    description: "Select a single .zip containing one or more metadata .xlsx spreadsheet files.",
                                                    inputId: "metaZipInput",
                                                    accept: ".zip",
                                                    multiple: false,
                                                    files: metaZipFile,
                                                    loading: metaZipLoading,
                                                    result: metaZipResult,
                                                    onFileChange: e => this.setState({ metaZipFile: e.target.files[0] || null, metaZipResult: null }),
                                                    onSubmit: this.handleMetaZipSubmit
                                                })}
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                            </Col>
                        </Row>
                    </Container>
                </section>
            </>
        );
    }
}

function withRouterParamsAndNavigate(Component) {
    return function Wrapped(props) {
        const navigate = useNavigate();
        const params = useParams();
        return <Component {...props} navigate={navigate} params={params} />;
    };
}

export default withRouterParamsAndNavigate(AdminImport);
